import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { reportInfo, reportWarning } from '@/lib/logging';
import {
  CAPABILITY_MEDIA_SELECTORS,
  scrubCapabilityBreadcrumb,
  scrubCapabilityUrls,
} from '@/lib/capability-redact';

// Initialize Sentry for frontend error monitoring
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
  const release =
    import.meta.env.VITE_SENTRY_RELEASE ||
    import.meta.env.VITE_GIT_COMMIT_SHA ||
    import.meta.env.VITE_COMMIT_SHA;
  const tracesSampleRate = environment === 'production' ? 0.1 : 1.0;
  
  // Skip Sentry in development environment
  if (environment === 'development') {
    reportInfo('Sentry disabled in development environment');
    return;
  }
  
  // Only initialize Sentry if DSN is provided
  if (!dsn || dsn === 'your_sentry_dsn_here') {
    reportWarning('Sentry DSN not configured - error monitoring disabled');
    return;
  }

  const integrations = [
    // Use the built-in browser tracing integration
    Sentry.browserTracingIntegration(),
    // Add React Router v6 integration
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // Add session replay integration for visual bug reproduction
    Sentry.replayIntegration({
      maskAllText: true, // Avoid leaking PII in session replays
      blockAllMedia: false, // Allow media for better context
      maskAllInputs: true, // Always mask input fields for security
      // Partner artwork capability links are bearer links: block any media
      // that points at one, and scrub them from Replay's own events
      // (navigation, network and performance entries).
      block: [...CAPABILITY_MEDIA_SELECTORS],
      beforeAddRecordingEvent: (event) => scrubCapabilityUrls(event),
    }),
  ];

  // Add browser profiling integration if available
  if ('browserProfilingIntegration' in Sentry) {
    integrations.push(Sentry.browserProfilingIntegration());
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations,

    // Performance monitoring
    tracesSampleRate,

    // Profiling sampling (profile same percentage as traces)
    ...(('browserProfilingIntegration' in Sentry) && { profilesSampleRate: tracesSampleRate }),

    // Session replay sampling
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
    replaysOnErrorSampleRate: 1.0, // Always capture replay when an error occurs

    // Session tracking
    beforeSend(event, hint) {
      // Don't send events for development errors we expect
      if (environment === 'development') {
        const error = hint.originalException;
        if (error instanceof Error) {
          // Filter out common development errors
          if (error.message.includes('Non-Error promise rejection')) {
            return null;
          }
        }
      }
      return scrubCapabilityUrls(event);
    },

    // Transactions carry raw urls on their name, spans and breadcrumbs.
    beforeSendTransaction(event) {
      return scrubCapabilityUrls(event);
    },

    // tRPC GET batching puts the full input JSON (incl. raw QR tokens) in the
    // query string; strip it from fetch/xhr breadcrumbs so request traces stay
    // useful without leaking scan payloads.
    beforeBreadcrumb(breadcrumb) {
      const isTrpcRequest =
        (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
        typeof breadcrumb.data?.url === 'string' &&
        breadcrumb.data.url.includes('/trpc');
      if (isTrpcRequest) {
        try {
          const url = new URL(breadcrumb.data!.url as string, window.location.origin);
          breadcrumb.data!.url = `${url.origin}${url.pathname}`;
        } catch {
          // Malformed URL — leave the breadcrumb as-is rather than drop it.
        }
      }
      return scrubCapabilityBreadcrumb(breadcrumb);
    },

    // Set initial scope
    initialScope: {
      tags: {
        component: 'threadheads-frontend',
        service: 'threadheads-frontend',
        release: release || 'unspecified',
      },
    },
  });

  if (release) {
    Sentry.setTag('release', release);
  }

  reportInfo(`Sentry initialized for ${environment} environment`, { environment, release });
};

// Set user context for tracking
export const setSentryUser = (userId: string, locationId?: string, locationName?: string, userEmail?: string, userRole?: string) => {
  Sentry.setUser({
    id: userId,
    username: userId,
    email: userEmail,
  });

  // Set user role tag if provided
  if (userRole) {
    Sentry.setTag('userRole', userRole);
  }

  // Set location context if provided
  if (locationId && locationName) {
    Sentry.setTag('locationId', locationId);
    Sentry.setTag('locationName', locationName);
    
    Sentry.setContext('location', {
      id: locationId,
      name: locationName,
    });
  }
};

// Add business context to errors
export const addBusinessContext = (context: {
  operation?: string;
  sku?: string;
  orderId?: string;
  poId?: string;
  [key: string]: unknown;
}) => {
  Sentry.setContext('business', context);
};

// Clear user context (for logout, location switch, etc.)
export const clearSentryContext = () => {
  Sentry.setUser(null);
  Sentry.setContext('business', null);
  Sentry.setContext('location', null);
};
